import React, { useEffect, useState, useRef } from 'react';
import '../Styles/gallery.css';

// ------------------------------------------------------------------
// STATIC GALLERY DATA (frontend only - no backend / axios needed)
// Replace the image_url values below with your own images.
// Easiest way: put your images inside src/assets/gallery/ and import
// them like:  import img1 from '../assets/gallery/img1.jpg';
// then use image_url: img1  (instead of a string path)
// ------------------------------------------------------------------
const vnkGalleryData = [
  {
    name: 'HR Confluence 2026',
    updatedAt: '2026-06-15',
    images: [
      { id: 1, image_url: 'https://picsum.photos/id/1011/800/600' },
      { id: 2, image_url: 'https://picsum.photos/id/1015/800/600' },
      { id: 3, image_url: 'https://picsum.photos/id/1016/800/600' },
      { id: 4, image_url: 'https://picsum.photos/id/1018/800/600' },
    ],
  },
  {
    name: 'Team Outing',
    updatedAt: '2026-05-10',
    images: [
      { id: 5, image_url: 'https://picsum.photos/id/1024/800/600' },
      { id: 6, image_url: 'https://picsum.photos/id/1025/800/600' },
      { id: 7, image_url: 'https://picsum.photos/id/1027/800/600' },
    ],
  },
  {
    name: 'Outstanding Workplaces Awards',
    updatedAt: '2026-04-02',
    images: [
      { id: 8, image_url: 'https://picsum.photos/id/1035/800/600' },
      { id: 9, image_url: 'https://picsum.photos/id/1039/800/600' },
      { id: 10, image_url: 'https://picsum.photos/id/1041/800/600' },
    ],
  },
];

const VnkGallery = () => {
  const [images, setImages] = useState([]);
  const [categoriesList, setCategoriesList] = useState([{ key: 'all', label: 'All' }]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [zoomed, setZoomed] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const modalImageRef = useRef(null);
  const modalContentRef = useRef(null);

  // Build images and categories from the static data (no API call)
  useEffect(() => {
    buildVnkImagesAndCategories();
    // eslint-disable-next-line
  }, [selectedCategory]);

  const buildVnkImagesAndCategories = () => {
    let allImages = [];
    let categories = [];
    vnkGalleryData.forEach(gallery => {
      if (gallery.images && gallery.images.length > 0) {
        const key = gallery.name ? gallery.name.toLowerCase().replace(/\s+/g, '_') : '';
        categories.push({
          key,
          label: gallery.name,
          updatedAt: gallery.updatedAt || '',
        });
      }
      (gallery.images || []).forEach(img => {
        allImages.push({
          ...img,
          category: gallery.name ? gallery.name.toLowerCase().replace(/\s+/g, '_') : 'all',
          galleryName: gallery.name,
        });
      });
    });
    // Sort images by id descending (newest first)
    allImages.sort((a, b) => b.id - a.id);
    // Find and remove 'Outstanding Workplaces Awards' if it exists
    const awardsKey = 'outstanding_workplaces_awards';
    let awardsCategory = null;
    const otherCategories = [];
    categories.forEach(cat => {
      if (cat.key === awardsKey) {
        awardsCategory = cat;
      } else {
        otherCategories.push(cat);
      }
    });
    // Sort the rest by updatedAt descending (newest first)
    otherCategories.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
    // Build categories list: All, then Awards (if exists), then the rest
    let categoriesArr = [{ key: 'all', label: 'All' }];
    if (awardsCategory) {
      categoriesArr.push({ key: awardsCategory.key, label: awardsCategory.label });
    }
    categoriesArr = [
      ...categoriesArr,
      ...otherCategories.map(({ key, label }) => ({ key, label }))
    ];
    setCategoriesList(categoriesArr);
    if (selectedCategory === 'all') {
      setImages(allImages);
    } else {
      setImages(allImages.filter(img => img.category === selectedCategory));
    }
  };

  // Modal controls
  const openVnkModal = idx => {
    setCurrentIndex(idx);
    setModalOpen(true);
    setZoomed(false);
  };
  const closeVnkModal = () => {
    setModalOpen(false);
    setZoomed(false);
  };
  const showVnkPrev = e => {
    e.stopPropagation();
    setCurrentIndex(currentIndex > 0 ? currentIndex - 1 : images.length - 1);
  };
  const showVnkNext = e => {
    e.stopPropagation();
    setCurrentIndex(currentIndex < images.length - 1 ? currentIndex + 1 : 0);
  };

  // Fullscreen
  const handleVnkFullscreen = () => {
    if (modalContentRef.current) {
      if (modalContentRef.current.requestFullscreen) {
        modalContentRef.current.requestFullscreen();
      }
    }
  };

  // Download
  const handleVnkDownload = () => {
    const img = images[currentIndex];
    const imageUrl = img.image_url;
    const fileName = imageUrl.split('/').pop() || `image-${img.id}.jpg`;
    fetch(imageUrl, { mode: 'cors' })
      .then(response => response.blob())
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      .catch(() => {
        alert('Failed to download image.');
      });
  };

  // Zoom
  const handleVnkZoom = () => setZoomed(z => !z);

  // Keyboard navigation
  useEffect(() => {
    if (!modalOpen) return;
    const handleVnkKey = e => {
      if (e.key === 'ArrowLeft') showVnkPrev(e);
      if (e.key === 'ArrowRight') showVnkNext(e);
      if (e.key === 'Escape') closeVnkModal();
    };
    window.addEventListener('keydown', handleVnkKey);
    return () => window.removeEventListener('keydown', handleVnkKey);
  }, [modalOpen, currentIndex, images.length]);

  // Swipe navigation
  useEffect(() => {
    if (!modalOpen) return;
    let startX = 0;
    const handleVnkTouchStart = e => {
      startX = e.touches[0].clientX;
    };
    const handleVnkTouchEnd = e => {
      const endX = e.changedTouches[0].clientX;
      if (startX - endX > 50) showVnkNext(e);
      if (endX - startX > 50) showVnkPrev(e);
    };
    const modalBody = document.querySelector('.modal-body');
    if (modalBody) {
      modalBody.addEventListener('touchstart', handleVnkTouchStart);
      modalBody.addEventListener('touchend', handleVnkTouchEnd);
      return () => {
        modalBody.removeEventListener('touchstart', handleVnkTouchStart);
        modalBody.removeEventListener('touchend', handleVnkTouchEnd);
      };
    }
  }, [modalOpen, currentIndex, images.length]);

  // Pan image when zoomed
  const handleVnkPan = e => {
    if (!zoomed || !modalImageRef.current) return;
    const rect = modalImageRef.current.getBoundingClientRect();
    let x, y;
    if (e.type === 'mousemove') {
      x = e.clientX;
      y = e.clientY;
    } else if (e.type === 'touchmove') {
      x = e.touches[0].clientX;
      y = e.touches[0].clientY;
    }
    const xPercent = ((x - rect.left) / rect.width) * 100;
    const yPercent = ((y - rect.top) / rect.height) * 100;
    modalImageRef.current.style.transformOrigin = `${xPercent}% ${yPercent}%`;
  };

  // Double click/tap to zoom
  let lastTap = 0;
  const handleVnkDoubleTap = e => {
    const now = Date.now();
    if (now - lastTap < 300) {
      handleVnkZoom();
    }
    lastTap = now;
  };

 return (
    <div className="vnk-gallery-page">
      {/* Category Dropdown */}
      <div className="vnk-gallery-dropdown-wrapper">
        <h2 className="vnk-gallery-heading">All</h2>
        <div className="vnk-gallery-dropdown-container">
          <div className="vnk-gallery-dropdown-trigger" onClick={() => setDropdownOpen(prev => !prev)}>
            <span>{categoriesList.find(c => c.key === selectedCategory)?.label || 'All'}</span>
            <i className={`fas fa-chevron-${dropdownOpen ? 'up' : 'down'}`}></i>
          </div>
          {dropdownOpen && (
            <div className="vnk-gallery-dropdown-menu">
              {categoriesList.map(cat => (
                <div
                  key={cat.key}
                  className={`vnk-gallery-dropdown-item${selectedCategory === cat.key ? ' active' : ''}`}
                  onClick={() => { setSelectedCategory(cat.key); setDropdownOpen(false); }}
                >
                  {cat.label}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Image Grid */}
      <div className="vnk-gallery-grid">
        {images.length > 0 ? (
          images.map((img, idx) => (
            <div key={img.id || idx} className="vnk-gallery-item" onClick={() => openVnkModal(idx)}>
              <img
                src={img.image_url}
                alt={img.galleryName}
                className="vnk-gallery-thumb"
              />
            </div>
          ))
        ) : (
          <p>No images found.</p>
        )}
      </div>

      {/* Modal */}
      {modalOpen && images.length > 0 && (
        <div className="modal" style={{ display: 'block' }} tabIndex={-1}>
          <div className="modal-dialog modal-dialog-centered vnk-modal-dialog-wrap m-0">
            <div className={`modal-content vnk-modal-content-wrap${zoomed ? ' vnk-zoomed-in' : ''}`} ref={modalContentRef}>
              <div className="modal-header" style={{ borderBottom: 'none' }}>
                <h5 className="modal-title">
                  <span id="currentImage">{currentIndex + 1}</span> / <span id="totalImages">{images.length}</span>
                </h5>
                <div className="vnk-modal-header-buttons">
                  <button type="button" className="btn" title="Fullscreen" onClick={handleVnkFullscreen}>
                    <i className="fas fa-expand"></i>
                  </button>
                  <button type="button" className="btn" title="Zoom" onClick={handleVnkZoom}>
                    <i className={`fas ${zoomed ? 'fa-search-minus' : 'fa-search-plus'}`}></i>
                  </button>
                  <button type="button" className="vnk-share-btn" title="Download" onClick={handleVnkDownload}>
                    <i className="fas fa-download"></i>
                  </button>
                  <button type="button" className="close" aria-label="Close" onClick={closeVnkModal}>
                    <span>&times;</span>
                  </button>
                </div>
              </div>
              <div
                className="modal-body text-center"
                onMouseMove={handleVnkPan}
                onTouchMove={handleVnkPan}
                onDoubleClick={handleVnkZoom}
                onTouchEnd={handleVnkDoubleTap}
              >
                <img
                  ref={modalImageRef}
                  id="modalImage"
                  src={images[currentIndex].image_url}
                  alt="Image"
                  className={`img-fluid${zoomed ? ' vnk-zoomed-in' : ''}`}
                  style={zoomed ? { transform: 'scale(2)', cursor: 'move' } : { transform: '', cursor: 'pointer' }}
                  onClick={handleVnkZoom}
                />
                <button type="button" className="prev" id="prevImage" onClick={showVnkPrev}>
                  <i className="fas fa-chevron-left"></i>
                </button>
                <button type="button" className="next" id="nextImage" onClick={showVnkNext}>
                  <i className="fas fa-chevron-right"></i>
                </button>
              </div>
              <div className="modal-footer" style={{ borderTop: 'none' }}>
                <div className="vnk-modal-caption">{images[currentIndex].galleryName}</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
export default VnkGallery; 